import useAutomationValues from './useAutomationValues';

import {
  getCustomAttributeInputType,
  filterCustomAttributes,
  getStandardAttributeInputType,
  isCustomAttribute,
} from 'dashboard/helper/automationHelper';

export function useEditableAutomation() {
  const { getConditionDropdownValues, getActionDropdownValues } =
    useAutomationValues();

  /**
   * This function sets the conditions for automation.
   * It help to format the conditions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing conditions to manifest.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @returns {Array} An array of manifested conditions.
   */
  const manifestConditions = (
    automation,
    allCustomAttributes,
    automationTypes
  ) => {
    // Si es modo branched, no hay conditions en el nivel superior
    if (automation.execution_mode === 'branched') {
      return [];
    }

    // Validar que conditions sea un array
    if (!automation.conditions || !Array.isArray(automation.conditions)) {
      return [];
    }

    const customAttributes = filterCustomAttributes(allCustomAttributes);
    return automation.conditions.map(condition => {
      const customAttr = isCustomAttribute(
        customAttributes,
        condition.attribute_key
      );
      let inputType = 'plain_text';
      if (customAttr) {
        inputType = getCustomAttributeInputType(customAttr.type);
      } else {
        inputType = getStandardAttributeInputType(
          automationTypes,
          automation.event_name,
          condition.attribute_key
        );
      }
      if (
        inputType === 'plain_text' ||
        inputType === 'date' ||
        inputType === 'number'
      ) {
        return { ...condition, values: condition.values[0] };
      }
      if (inputType === 'comma_separated_plain_text') {
        return { ...condition, values: condition.values.join(',') };
      }
      return {
        ...condition,
        query_operator: condition.query_operator || 'and',
        values: [...getConditionDropdownValues(condition.attribute_key)].filter(
          item => [...condition.values].includes(item.id)
        ),
      };
    });
  };

  /**
   * Generates an array of actions for the automation.
   * @param {Object} action - The action object.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array|Object} Generated actions array or object based on input type.
   */
  const generateActionsArray = (action, automationActionTypes) => {
    const params = action.action_params;
    const inputType = automationActionTypes.find(
      item => item.key === action.action_name
    ).inputType;
    if (inputType === 'multi_select' || inputType === 'search_select') {
      return [...getActionDropdownValues(action.action_name)].filter(item =>
        [...params].includes(item.id)
      );
    }
    if (inputType === 'team_message') {
      return {
        team_ids: [...getActionDropdownValues(action.action_name)].filter(
          item => [...params[0].team_ids].includes(item.id)
        ),
        message: params[0].message,
      };
    }
    return [...params];
  };

  /**
   * This function sets the actions for automation.
   * It help to format the actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object containing actions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Array} An array of manifested actions.
   */
  const manifestActions = (automation, automationActionTypes) => {
    // Si es modo branched, no hay actions en el nivel superior
    if (automation.execution_mode === 'branched') {
      return [];
    }

    // Validar que actions sea un array
    if (!automation.actions || !Array.isArray(automation.actions)) {
      return [];
    }

    return automation.actions.map(action => ({
      ...action,
      action_params: action.action_params.length
        ? generateActionsArray(action, automationActionTypes)
        : [],
    }));
  };

  /**
   * Formatea las condiciones de una branch (similar a manifestConditions pero para branches).
   * @param {Array} conditions - Las condiciones a formatear.
   * @param {Array} allCustomAttributes - Lista de custom attributes.
   * @param {Object} automationTypes - Tipos de automatización.
   * @param {string} eventName - Nombre del evento.
   * @returns {Array} Condiciones formateadas.
   */
  const formatBranchConditions = (
    conditions,
    allCustomAttributes,
    automationTypes,
    eventName
  ) => {
    if (!Array.isArray(conditions)) return [];

    const customAttributes = filterCustomAttributes(allCustomAttributes);
    return conditions.map(condition => {
      const customAttr = isCustomAttribute(
        customAttributes,
        condition.attribute_key
      );
      let inputType = 'plain_text';
      if (customAttr) {
        inputType = getCustomAttributeInputType(customAttr.type);
      } else {
        inputType = getStandardAttributeInputType(
          automationTypes,
          eventName,
          condition.attribute_key
        );
      }

      if (
        inputType === 'plain_text' ||
        inputType === 'date' ||
        inputType === 'number'
      ) {
        return {
          ...condition,
          values: Array.isArray(condition.values)
            ? condition.values[0] || ''
            : condition.values || '',
        };
      }
      if (inputType === 'comma_separated_plain_text') {
        return {
          ...condition,
          values: Array.isArray(condition.values)
            ? condition.values.join(',')
            : condition.values || '',
        };
      }
      // Para multi_select y search_select, formatear con dropdown values
      const dropdownValues = getConditionDropdownValues(
        condition.attribute_key
      );

      // Si dropdownValues tiene items, filtrar por id
      if (dropdownValues && dropdownValues.length > 0) {
        // Normalizar values a array de IDs
        let valueIds = [];
        if (Array.isArray(condition.values)) {
          // Si values es un array, extraer los IDs
          valueIds = condition.values.map(v =>
            typeof v === 'object' && v.id ? v.id : v
          );
        } else if (
          typeof condition.values === 'object' &&
          condition.values.id
        ) {
          // Si values es un objeto con id, usar ese id
          valueIds = [condition.values.id];
        } else if (condition.values) {
          // Si values es un valor simple, usarlo directamente
          valueIds = [condition.values];
        }

        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: [...dropdownValues].filter(item =>
            valueIds.includes(item.id)
          ),
        };
      }

      // Si no hay dropdownValues, devolver los valores tal cual
      return {
        ...condition,
        query_operator: condition.query_operator || 'and',
        values: Array.isArray(condition.values) ? condition.values : [],
      };
    });
  };

  /**
   * Formatea las acciones de una branch (similar a manifestActions pero para branches).
   * @param {Array} actions - Las acciones a formatear.
   * @param {Array} automationActionTypes - Tipos de acciones de automatización.
   * @returns {Array} Acciones formateadas.
   */
  const formatBranchActions = (actions, automationActionTypes) => {
    if (!Array.isArray(actions)) return [];

    return actions.map(action => {
      const actionType = automationActionTypes.find(
        item => item.key === action.action_name
      );

      if (!actionType) {
        return {
          ...action,
          action_params: Array.isArray(action.action_params)
            ? [...action.action_params]
            : [],
        };
      }

      const inputType = actionType.inputType;
      const params = action.action_params;

      // Para multi_select y search_select
      if (inputType === 'multi_select' || inputType === 'search_select') {
        const dropdownValues = getActionDropdownValues(action.action_name);
        if (Array.isArray(params) && params.length) {
          // Si dropdownValues tiene items, filtrar por id
          if (dropdownValues && dropdownValues.length > 0) {
            return {
              ...action,
              action_params: [...dropdownValues].filter(item =>
                [...params].includes(item.id)
              ),
            };
          }
          // Si no hay dropdownValues, devolver params tal cual
          return {
            ...action,
            action_params: [...params],
          };
        }
        return {
          ...action,
          action_params: [],
        };
      }

      // Para team_message
      if (inputType === 'team_message') {
        if (params && params[0]) {
          const teamParams = params[0];
          const dropdownValues = getActionDropdownValues(action.action_name);
          return {
            ...action,
            action_params: {
              team_ids: [...dropdownValues].filter(item =>
                [...(teamParams.team_ids || [])].includes(item.id)
              ),
              message: teamParams.message || '',
            },
          };
        }
        return {
          ...action,
          action_params: { team_ids: [], message: '' },
        };
      }

      // Para textarea (send_message, add_private_note, etc.)
      if (inputType === 'textarea') {
        // El mensaje viene como string en params[0] o directamente como string
        const message = Array.isArray(params) ? params[0] || '' : params || '';
        return {
          ...action,
          action_params: [message],
        };
      }

      // Para otros tipos, devolver params tal cual
      return {
        ...action,
        action_params: Array.isArray(params) ? [...params] : [],
      };
    });
  };

  /**
   * Normaliza y formatea una branch recursivamente.
   * @param {Object} branch - La branch a normalizar.
   * @param {Array} allCustomAttributes - Lista de custom attributes.
   * @param {Object} automationTypes - Tipos de automatización.
   * @param {Array} automationActionTypes - Tipos de acciones.
   * @param {string} eventName - Nombre del evento.
   * @returns {Object} La branch normalizada y formateada.
   */
  const normalizeBranch = (
    branch,
    allCustomAttributes,
    automationTypes,
    automationActionTypes,
    eventName
  ) => {
    const normalized = {
      id: branch.id,
      name: branch.name || '',
      conditions: formatBranchConditions(
        branch.conditions || [],
        allCustomAttributes,
        automationTypes,
        eventName
      ),
      actions: formatBranchActions(branch.actions || [], automationActionTypes),
      branches: Array.isArray(branch.branches) ? branch.branches : [],
    };

    // Normalizar recursivamente las sub-branches
    if (normalized.branches.length > 0) {
      normalized.branches = normalized.branches.map(subBranch =>
        normalizeBranch(
          subBranch,
          allCustomAttributes,
          automationTypes,
          automationActionTypes,
          eventName
        )
      );
    }

    return normalized;
  };

  /**
   * Formats the automation object for use when we edit the automation.
   * It help to format the conditions and actions for the automation when we open the edit automation modal.
   * @param {Object} automation - The automation object to format.
   * @param {Array} allCustomAttributes - List of all custom attributes.
   * @param {Object} automationTypes - Object containing automation type definitions.
   * @param {Array} automationActionTypes - List of available automation action types.
   * @returns {Object} A new object with formatted automation data, including automation conditions and actions.
   */
  const formatAutomation = (
    automation,
    allCustomAttributes,
    automationTypes,
    automationActionTypes
  ) => {
    const formatted = {
      ...automation,
      conditions: manifestConditions(
        automation,
        allCustomAttributes,
        automationTypes
      ),
      actions: manifestActions(automation, automationActionTypes),
    };

    // Asegurar que branches exista para modo branched y normalizar cada branch
    if (automation.execution_mode === 'branched') {
      if (!formatted.branches || !Array.isArray(formatted.branches)) {
        formatted.branches = [];
      } else {
        formatted.branches = formatted.branches.map(branch =>
          normalizeBranch(
            branch,
            allCustomAttributes,
            automationTypes,
            automationActionTypes,
            automation.event_name
          )
        );
      }
    }

    return formatted;
  };

  return { formatAutomation };
}
